/**
 * "Fix it" from the public inspection lookup (core fixit.ts; owner, 2026-10-13). The ask under a cited item on cleancleaver.com/find/
 * links to …/app/?signup=1&place=<place key>&fix=<FDA item> ("Are you <place>? Fix it") or …&fix=<item> ("Run a restaurant?"). Kept
 * here the moment the app loads (like the level and buy intents), so it survives Google's sign-in window, the email code and a
 * reload. Setup then types the place's name into the search (the owner taps their own place: a link never opens "Is this you?"
 * by itself, security run-12 F1), and the ready screen's first button is the item's plan. Only a well-formed key and an item 1–56 are kept (core fixFromQuery), for one day; cleared once used.
 */
import { FIX_PARAMS, fixFromQuery, type FixAs } from "@logwave/core";

export const FIX_KEY = "cc.fix.intent";
export const FIX_TTL_MS = 24 * 3_600_000;
export interface FixIntent { place: string | null; item: number; /** "I work there" (the site's staff choice) or absent (the owner's way). */ as?: FixAs; /** "For my home": the matching home guide (or "1" for none): setup is the home's, and it lands on that guide. */ home?: string }

function safeStorage(): Storage | undefined {
  try { return typeof localStorage === "undefined" ? undefined : localStorage; } catch { return undefined; }
}
let memory: (FixIntent & { at: string; shown?: true }) | null = null;

/** A visit from a "Fix it" ask: keep it. Returns it, or null for any other visit (or a malformed one). */
export function captureFix(search: string, storage: Storage | undefined = safeStorage(), now = new Date()): FixIntent | null {
  const f = fixFromQuery(search);
  if (!f) return null;
  memory = { ...f, at: now.toISOString() };
  try { storage?.setItem(FIX_KEY, JSON.stringify(memory)); } catch { /* this page load only */ }
  return f;
}

/** The waiting intent, re-checked on every read (a value stored by hand is dropped), or null when none or too old. */
export function pendingFix(storage: Storage | undefined = safeStorage(), now = new Date()): FixIntent | null {
  let v: { place?: unknown; item?: unknown; home?: unknown; as?: unknown; at?: unknown } | null;
  try { v = storage ? JSON.parse(storage.getItem(FIX_KEY) ?? "null") : memory; } catch { v = memory; }
  if (!v || typeof v.at !== "string" || typeof v.item !== "number") return null;
  const at = Date.parse(v.at);
  if (!Number.isFinite(at) || now.getTime() - at > FIX_TTL_MS || at - now.getTime() > 60_000) return null;
  const q = new URLSearchParams({ [FIX_PARAMS.item]: String(v.item), ...(typeof v.place === "string" ? { [FIX_PARAMS.place]: v.place } : {}), ...(typeof v.home === "string" ? { [FIX_PARAMS.home]: v.home } : {}), ...(typeof v.as === "string" ? { [FIX_PARAMS.as]: v.as } : {}) });
  return fixFromQuery(q.toString());
}

/** The place is used (setup opened it): keep the item for the ready screen. */
export function usedFixPlace(storage: Storage | undefined = safeStorage()): void {
  const f = pendingFix(storage);
  if (!f) return;
  const v = { item: f.item, place: null, ...(f.home ? { home: f.home } : {}), ...(fixShown(storage) ? { shown: true as const } : {}), at: new Date().toISOString() };
  memory = v;
  try { storage?.setItem(FIX_KEY, JSON.stringify(v)); } catch { /* ignore */ }
}

/** "I work there" from the signed-in card: the same intent as the site's staff link (as=work), so setup opens on "Are you a manager or on staff?". */
export function markFixWork(storage: Storage | undefined = safeStorage()): void {
  const f = pendingFix(storage);
  if (!f?.place) return;
  const v = { ...f, as: "work" as const, at: new Date().toISOString(), ...(fixShown(storage) ? { shown: true as const } : {}) };
  memory = v;
  try { storage?.setItem(FIX_KEY, JSON.stringify(v)); } catch { /* this page load only */ }
}

/**
 * "Fix and log it now" (core fixgo.ts; owner, 2026-10-15): the first screen the intent opens (screens/FixArrive.tsx) marks it shown, so
 * the one redirect from Today ("/") to the fix happens once per tap on the site, never every time the person opens the app (the
 * not-yet-claimed card keeps the intent while they set the place up).
 */
export function markFixShown(storage: Storage | undefined = safeStorage()): void {
  const f = pendingFix(storage);
  if (!f) return;
  const v = { ...f, at: new Date().toISOString(), shown: true as const };
  memory = v;
  try { storage?.setItem(FIX_KEY, JSON.stringify(v)); } catch { /* this page load only */ }
}
export function fixShown(storage: Storage | undefined = safeStorage()): boolean {
  try { const v = storage ? JSON.parse(storage.getItem(FIX_KEY) ?? "null") : memory; return !!v && v.shown === true; } catch { return !!memory?.shown; }
}

export function clearFix(storage: Storage | undefined = safeStorage()): void {
  memory = null;
  try { storage?.removeItem(FIX_KEY); } catch { /* ignore */ }
}

/** The address without the fix parameters (signup=1 stays). */
export function withoutFixParams(search: string): string {
  const p = new URLSearchParams(search);
  p.delete(FIX_PARAMS.place);
  p.delete(FIX_PARAMS.item);
  p.delete(FIX_PARAMS.home);
  p.delete(FIX_PARAMS.as);
  const s = p.toString();
  return s ? `?${s}` : "";
}
